import { MouseEvent } from 'react';
import { ReactComponent as MinusIcon } from '@assets/icons/minus.svg';
import { ReactComponent as PlusIcon } from '@assets/icons/plus.svg';

import {
  Button,
  ButtonProps,
  HStack,
  NumberInputProps,
  StackProps,
  SystemStyleObject,
  Text,
  TextProps,
  useNumberInput,
  useTheme
} from '@chakra-ui/react';

interface CounterProps extends Omit<NumberInputProps, 'onChange'> {
  minValue?: number;
  maxValue?: number;
  isAlternativeDesign?: boolean;
  value: number;
  onChange: (value: number) => void;
  buttonProps?: ButtonProps;
  containerProps?: StackProps;
  textProps?: TextProps;
  // eslint-disable-next-line
  styles?: { [key: string]: any };
}

const Counter = ({
  minValue = 0,
  maxValue = 99,
  onChange,
  value,
  isAlternativeDesign = false,
  buttonProps = {},
  textProps = {},
  containerProps = {},
  styles = {},
  ...props
}: CounterProps) => {
  const {
    colors: {
      palette: {
        components: {
          ui: { counter: palette }
        }
      }
    }
  } = useTheme();
  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      step: 1,
      min: minValue,
      max: maxValue,
      precision: 0,
      defaultValue: 0,
      value,
      ...props
    });

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps();

  const extraStyles = isAlternativeDesign
    ? {
        minW: 0,
        '& svg': {
          color: palette.counterSvgColor
        }
      }
    : ({} as SystemStyleObject);
  const buttonColorScheme = isAlternativeDesign ? 'secondary' : 'primaryLight';
  const buttonVariant = isAlternativeDesign ? 'text' : 'brand';
  const buttonPadding = isAlternativeDesign ? '0' : '12px 16px';
  const buttonSize = isAlternativeDesign
    ? { base: '30px', md: '37px' }
    : { base: '46px', md: '50px' };

  const handleIncrease = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    const newValue = Number(value) + 1;
    if (newValue <= maxValue) {
      onChange(newValue);
    }
  };

  const handleDecrease = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    const newValue = Number(value) - 1;
    if (newValue >= minValue) {
      onChange(newValue);
    }
  };

  return (
    <HStack
      gap={
        isAlternativeDesign
          ? { base: '20px', md: '15px', lg: '15px' }
          : { base: '10px', md: '15px', lg: '20px' }
      }
      {...containerProps}
    >
      <Button
        {...dec}
        onClick={handleDecrease}
        variant={buttonVariant}
        colorScheme={buttonColorScheme}
        w={buttonSize}
        h={buttonSize}
        p={buttonPadding}
        borderRadius='6px'
        sx={{ ...extraStyles, ...styles }}
        {...buttonProps}
      >
        <MinusIcon width={17} height={22} />
      </Button>

      <Text
        color={{
          base: palette.counterTextColorBase,
          lg: isAlternativeDesign
            ? palette.counterTextColorLgAlt
            : palette.counterTextColorLgDefault
        }}
        p={0}
        border={0}
        fontSize={{
          base: '20px',
          md: '24px',
          lg: isAlternativeDesign ? '27px' : '32px'
        }}
        pointerEvents='none'
        textAlign='center'
        {...textProps}
      >
        {input.value}
      </Text>
      <Button
        {...inc}
        onClick={handleIncrease}
        variant={buttonVariant}
        colorScheme={buttonColorScheme}
        w={buttonSize}
        h={buttonSize}
        p={buttonPadding}
        borderRadius='6px'
        sx={{ ...extraStyles, ...styles }}
        {...buttonProps}
      >
        <PlusIcon width={17} height={22} />
      </Button>
    </HStack>
  );
};

export default Counter;
